import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  writeBatch,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase/client.js';
import type {
  FeedbackMessage,
  FeedbackMessageRole,
} from '../../types/feedbackMessage.js';

function ts(value: unknown): Date {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  const v = value as { toDate?: () => Date };
  return v.toDate ? v.toDate() : new Date();
}

function toFeedbackMessage(
  snap: QueryDocumentSnapshot<DocumentData>,
): FeedbackMessage {
  const d = snap.data();
  return {
    id: snap.id,
    feedbackOwnerUid: String(d['feedbackOwnerUid'] ?? ''),
    authorUid: String(d['authorUid'] ?? ''),
    authorRole: (d['authorRole'] ?? 'user') as FeedbackMessageRole,
    body: String(d['body'] ?? ''),
    createdAt: ts(d['createdAt']),
  };
}

export interface FeedbackMessageInput {
  feedbackOwnerUid: string;
  authorUid: string;
  authorRole: FeedbackMessageRole;
  body: string;
}

/** スレッドのメッセージを古い順（チャット表示順）で取得 */
export async function list(feedbackId: string): Promise<FeedbackMessage[]> {
  const q = query(
    collection(db, `feedbacks/${feedbackId}/messages`),
    orderBy('createdAt', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => toFeedbackMessage(d));
}

/**
 * メッセージ追記。messages への新規 doc と、親 feedback の
 * lastMessageAt/lastMessageRole/updatedAt 更新（未読判定用 bump）を
 * writeBatch で原子的に実行する。Rules はこの2書き込みを個別に検証する
 * （messages.create と feedbacks.update のスレッド bump 分岐）。
 */
export async function create(
  feedbackId: string,
  input: FeedbackMessageInput,
): Promise<string> {
  const batch = writeBatch(db);
  const msgRef = doc(collection(db, `feedbacks/${feedbackId}/messages`));
  batch.set(msgRef, {
    feedbackOwnerUid: input.feedbackOwnerUid,
    authorUid: input.authorUid,
    authorRole: input.authorRole,
    body: input.body,
    createdAt: serverTimestamp(),
  });
  batch.update(doc(db, `feedbacks/${feedbackId}`), {
    lastMessageAt: serverTimestamp(),
    lastMessageRole: input.authorRole,
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
  return msgRef.id;
}
